// src/services/workItemService.js
//
// Universal Work Item API client
// ==============================
//
// PURPOSE
// -------
// This service provides a thin, stable frontend API for universal work item endpoints.
//
// Current backend contract:
//   GET /work-items/:key
//   -> { key: "todo:123" | "leave:9223372036854775807", type: "...", entity: { ... } }
//
// CURRENT ARCHITECTURE ALIGNMENT
// ------------------------------
// This file is intentionally THIN and should remain thin.
//
// Responsibilities of THIS file:
// - build the correct request URL
// - pass AbortSignal through to Axios
// - keep withCredentials enabled
// - perform light input / response-shape validation
//
// Responsibilities that DO NOT belong here:
// - auth refresh handling           -> owned by apiClient
// - local logout / softLogout       -> owned by page/AuthContext
// - business rules / permissions    -> owned by backend + page-level UI logic
// - UI-friendly error messaging     -> owned by the calling page/component
//
// WHY THIN SERVICES ARE GOOD HERE
// -------------------------------
// In your current app, the universal work item endpoint is already backend-driven.
// That means this service should not try to "understand" too much about todo/leave/etc.
// It should only fetch the authoritative DTO and return it.
//
// DESIGN GOALS
// ------------
// - universal key support
// - AbortSignal support
// - safe request URL construction
// - predictable response shape
// - no accidental auth/session side effects

import apiClient from "../utils/apiClient";
import { isRequestCanceled } from "./httpUtils";

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Normalize and validate a universal work item key.
 *
 * Expected examples:
 * - "todo:123"
 * - "leave:9223372036854775807"
 *
 * IMPORTANT:
 * - We keep this validation intentionally conservative but generic.
 * - The backend remains the final authority.
 * - This validation exists mainly to catch obviously broken caller input early.
 */
function normalizeWorkItemKey(workItemKey) {
  const key = String(workItemKey || "").trim();

  if (!key) {
    throw new Error("fetchWorkItem: workItemKey must be a non-empty string.");
  }

  // Conservative size guard against accidental garbage input.
  if (key.length > 128) {
    throw new Error("fetchWorkItem: workItemKey is too long.");
  }

  // Current universal contract expects exactly one ":".
  const parts = key.split(":");
  if (parts.length !== 2) {
    throw new Error('fetchWorkItem: workItemKey must match "<type>:<id>".');
  }

  const type = String(parts[0] || "").trim();
  const id = String(parts[1] || "").trim();

  if (!type || !id) {
    throw new Error('fetchWorkItem: workItemKey must match "<type>:<id>".');
  }

  return `${type}:${id}`;
}

/**
 * Build the backend URL for one work item.
 *
 * We always encode the key because:
 * - ":" appears in every work item key
 * - future work item keys may contain additional characters
 */
function buildWorkItemUrl(workItemKey) {
  const normalizedKey = normalizeWorkItemKey(workItemKey);
  const encodedKey = encodeURIComponent(normalizedKey);
  return `/work-items/${encodedKey}`;
}

/**
 * Validate the minimum response shape expected by callers.
 *
 * We do NOT deeply validate the entity, because:
 * - entity shape is intentionally type-specific
 * - backend is authoritative
 * - page-level rendering handles type-specific interpretation
 *
 * We only validate the universal envelope.
 */
function assertValidWorkItemResponse(data) {
  const isObject = data && typeof data === "object" && !Array.isArray(data);

  if (!isObject) {
    throw new Error("fetchWorkItem: backend returned an invalid response payload.");
  }

  const hasKey = typeof data.key === "string" && data.key.trim().length > 0;
  const hasType = typeof data.type === "string" && data.type.trim().length > 0;
  const hasEntity = Object.prototype.hasOwnProperty.call(data, "entity");

  if (!hasKey || !hasType || !hasEntity) {
    throw new Error("fetchWorkItem: backend response is missing required fields.");
  }

  return data;
}

/**
 * Decide whether this error should be logged in this low-level service.
 *
 * Philosophy:
 * - request cancellations are expected -> never log here
 * - normal 4xx business/auth errors are usually better handled at page level
 * - unexpected technical failures may still be useful to log in development
 */
function shouldLogError(error) {
  if (isRequestCanceled(error)) return false;

  const status = error?.response?.status;

  // Let pages/components decide how to present expected client-side failures.
  if (typeof status === "number" && status >= 400 && status < 500) {
    return DEV;
  }

  return true;
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Fetch universal work item details.
 *
 * Backend contract:
 *   GET /work-items/:key
 *
 * Response shape:
 *   {
 *     key: "todo:123" | "leave:9223372036854775807" | ...,
 *     type: "todo" | "leave" | ...,
 *     entity: { ...type-specific payload... }
 *   }
 *
 * @param {string} workItemKey
 *   Universal work item key, for example:
 *   - "todo:123"
 *   - "leave:9223372036854775807"
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 *   Optional request cancellation signal.
 *
 * @returns {Promise<{ key: string, type: string, entity: any }>}
 *
 * IMPORTANT:
 * - This function does NOT catch-and-transform auth/session failures.
 * - The caller should decide whether a 401 means:
 *     - softLogout
 *     - redirect
 *     - inline error
 */
export async function fetchWorkItem(workItemKey, options = {}) {
  try {
    const url = buildWorkItemUrl(workItemKey);

    const response = await apiClient.get(url, {
      withCredentials: true,
      signal: options.signal,
    });

    return assertValidWorkItemResponse(response?.data);
  } catch (error) {
    if (shouldLogError(error)) {
      // eslint-disable-next-line no-console
      console.error("[workItemService] fetchWorkItem failed:", error);
    }

    throw error;
  }
}

/* -------------------------------------------------------------------------- */
/* Future expansion notes                                                     */
/* -------------------------------------------------------------------------- */

/**
 * If later you extend the universal work item module, this file is the correct place
 * for additional THIN helpers such as:
 *
 * - fetchWorkItemHistory(workItemKey)
 * - fetchWorkItemPermissions(workItemKey)
 * - fetchWorkItemActivity(workItemKey)
 *
 * But keep the same rule:
 * - transport helpers only
 * - no UI logic
 * - no auth recovery logic
 * - no cross-tab/session decisions here
 */